import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiArrowLeft } from "react-icons/fi";
import api from "../utils/api";
import Loader from "../components/Loader";

const emptyForm = {
  title: "",
  desc: "",
  order: 0,
  published: true,
};

export default function OtherServiceForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api
        .get(`/other-services/admin/edit/${id}`)
        .then((res) => setForm(res.data))
        .catch(() => toast.error("Failed to load service"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (isEdit) {
        await api.put(`/other-services/${id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/other-services", payload);
        toast.success("Service created");
      }
      navigate("/admin/other-services");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="max-w-2xl">
      <Link
        to="/admin/other-services"
        className="inline-flex items-center gap-1 text-saffron-600 text-sm font-medium mb-6 hover:gap-2 transition-all"
      >
        <FiArrowLeft /> Back to Other Services
      </Link>

      <h1 className="font-heading text-2xl text-maroon-800 mb-6">
        {isEdit ? "Edit Service" : "Add New Service"}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Service Title *</label>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
            placeholder="e.g. Rudra Abhishek"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional, max 300 chars)</label>
          <textarea
            name="desc"
            value={form.desc}
            onChange={handleChange}
            rows={3}
            maxLength={300}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
            placeholder="Short description shown on the card"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
          <input
            type="number"
            name="order"
            value={form.order}
            onChange={handleChange}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
            placeholder="0"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="published" checked={form.published} onChange={handleChange} className="w-4 h-4" />
          Published (visible on website)
        </label>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 bg-maroon-700 hover:bg-maroon-800 disabled:opacity-60 text-gold-100 px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          <FiSave /> {saving ? "Saving..." : "Save Service"}
        </button>
      </form>
    </div>
  );
}
